import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  QrCode, Camera, ShieldCheck, Zap, X, ShoppingBag,
  Diamond, ChevronRight, Phone, User, Key, CheckCircle2, ArrowRight
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { shopService } from '../../services/shopService';
import { userService } from '../../services/userService';
import { showError } from '../../utils/alert';

const ScanPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isScanning, setIsScanning] = useState(false);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  // Flow State: 'intro' -> 'phone' -> 'register' -> 'otp' -> 'complete'
  const [step, setStep] = useState('intro');
  
  const shopIdFromUrl = searchParams.get('shopId');
  const campaignIdxFromUrl = searchParams.get('campaignIdx');
  const shopId = shopIdFromUrl || sessionStorage.getItem('currentShopId');

  // Form Data
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    if (shopId) {
      const fetchShop = async () => {
        // Fast path for returning customers if shop is cached
        const cachedShop = sessionStorage.getItem(`shop_${shopId}`);
        const savedPhone = sessionStorage.getItem('userPhone');
        
        if (cachedShop && savedPhone) {
          const shopData = JSON.parse(cachedShop);
          setShop(shopData);
          const activeIdx = campaignIdxFromUrl || shopData.activeCampaignIdx || 0;
          sessionStorage.setItem('currentCampaignIdx', activeIdx);
          const activeCampaign = shopData.campaigns?.[activeIdx];
          const targetPath = activeCampaign?.mode === 'stamp' ? '/stamp' : '/scratch';
          navigate(`${targetPath}?shopId=${shopId}`, { replace: true });
          return;
        }

        setLoading(true);
        try {
          const shopData = await shopService.getShopById(shopId);
          if (shopData) {
            setShop(shopData);
            sessionStorage.setItem('currentShopId', shopId);
            sessionStorage.setItem(`shop_${shopId}`, JSON.stringify(shopData));
            
            const activeIdx = campaignIdxFromUrl !== null ? campaignIdxFromUrl : (shopData.activeCampaignIdx || 0);
            sessionStorage.setItem('currentCampaignIdx', activeIdx);

            // Reset scratch session for a fresh QR scan
            sessionStorage.removeItem('hasScratched');
            sessionStorage.removeItem('currentReward');
            
            if (savedPhone) {
              const activeCampaign = shopData.campaigns?.[activeIdx];
              const targetPath = activeCampaign?.mode === 'stamp' ? '/stamp' : '/scratch';
              navigate(`${targetPath}?shopId=${shopId}`, { replace: true });
              return;
            }
            
            setStep('phone');
          }
        } catch (error) {
          console.error("Error fetching shop:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchShop();
    }
  }, [shopId, navigate, campaignIdxFromUrl]);

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (phone.length < 10) return showError('Invalid Identity', 'Please enter a valid mobile number.');
    
    setAuthLoading(true);
    try {
      const existingUser = await userService.getCustomerByPhone(phone);
      if (existingUser) {
        setName(existingUser.name);
        sessionStorage.setItem('userPhone', phone);
        sessionStorage.setItem('userName', existingUser.name);
        
        const activeIdx = sessionStorage.getItem('currentCampaignIdx') || 0;
        const activeCampaign = shop?.campaigns?.[activeIdx];
        const targetPath = activeCampaign?.mode === 'stamp' ? '/stamp' : '/scratch';
        
        navigate(`${targetPath}?shopId=${shopId}`);
      } else {
        setStep('register');
      }
    } catch (error) {
      showError('System Error', 'Unable to verify node.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!name) return showError('Missing Data', 'Please provide your name.');
    
    setAuthLoading(true);
    try {
      await userService.createCustomer({ phone, name });
      sessionStorage.setItem('userPhone', phone);
      sessionStorage.setItem('userName', name);
      
      const activeIdx = sessionStorage.getItem('currentCampaignIdx') || 0;
      const activeCampaign = shop?.campaigns?.[activeIdx];
      const targetPath = activeCampaign?.mode === 'stamp' ? '/stamp' : '/scratch';
      
      navigate(`${targetPath}?shopId=${shopId}`);
    } catch (error) {
      showError('System Error', 'Unable to create profile.');
    } finally {
      setAuthLoading(false);
    }
  };

  const startManualScan = () => {
    setIsScanning(true);
    setTimeout(() => {
      setIsScanning(false);
      setStep('phone');
    }, 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fdfdfd] flex flex-col items-center justify-center text-black">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }} className="mb-8">
          <Diamond size={40} className="text-[#d4af37]" />
        </motion.div>
        <p className="text-[10px] font-black uppercase tracking-[0.5em] animate-pulse">Syncing Merchant Node...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdfdfd] relative overflow-hidden flex flex-col items-center justify-center p-6">
      <div className="absolute inset-0 luxury-grid-bg pointer-events-none" />
      
      {/* Immersive Branding Elements */}
      <div className="absolute top-[-20%] left-[-10%] w-[800px] h-[800px] bg-[#d4af37]/5 rounded-full blur-[150px] animate-pulse pointer-events-none" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[800px] h-[800px] bg-black/[0.02] rounded-full blur-[150px] pointer-events-none" />

      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-xl z-10"
      >
        {/* Merchant Hero Branding */}
        <div className="text-center mb-16">

          <div className="space-y-4">
            <h1 className="text-6xl md:text-7xl font-serif text-black tracking-tighter leading-none mb-8">
              {shop ? (
                <span className="luxury-text italic text-gold drop-shadow-sm">{shop.shopName}</span>
              ) : (
                <span className="opacity-20 italic">Welcome</span>
              )}
            </h1>
            
            <div className="inline-flex items-center gap-4 px-10 py-3 rounded-full bg-black text-white text-[9px] font-black tracking-[0.5em] uppercase shadow-2xl">
              <div className="w-2 h-2 rounded-full bg-[#d4af37] animate-pulse" />
              Member Access Portal
            </div>
          </div>
        </div>

        {/* Identity Node Container */}
        <div className="luxury-card p-12 md:p-16 relative overflow-hidden backdrop-blur-3xl bg-white/90 border-white/50 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.1)]">
          {/* Internal Progress Protocol */}
          {step !== 'complete' && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-black/[0.03]">
              <motion.div 
                className="h-full bg-black"
                initial={{ width: '33%' }}
                animate={{ 
                  width: step === 'phone' ? '33%' : step === 'register' ? '66%' : '100%' 
                }}
              />
            </div>
          )}

          <AnimatePresence mode="wait">
            {step === 'phone' && (
              <motion.form key="phone" onSubmit={handlePhoneSubmit} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-12">
                <div className="text-center space-y-3">
                  <h3 className="text-3xl font-serif text-black italic">Welcome</h3>
                  <p className="text-[#999] text-[9px] font-black uppercase tracking-[0.4em]">
                    {shop?.campaigns?.[sessionStorage.getItem('currentCampaignIdx') || 0]?.mode === 'stamp' 
                      ? 'Register for Loyalty Stamps' 
                      : 'Enter Identity to Unlock Reward'}
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-[#d4af37]/20 to-black/5 rounded-[2.5rem] blur opacity-0 group-hover:opacity-100 transition duration-1000 group-hover:duration-200" />
                    <div className="relative">
                      <Phone className="absolute left-8 top-1/2 -translate-y-1/2 text-[#ccc]" size={20} />
                      <input 
                        type="tel" required placeholder="+91 00000 00000"
                        value={phone} onChange={(e) => setPhone(e.target.value)}
                        className="w-full bg-white border border-black/[0.1] rounded-[2rem] py-7 pl-20 pr-10 text-xl focus:outline-none focus:border-black transition-all shadow-sm font-semibold tracking-wide"
                      />
                    </div>
                  </div>
                </div>
                <button disabled={authLoading} className="luxury-button w-full flex items-center justify-center gap-4 !py-8 shadow-2xl !text-[11px] tracking-[0.3em]">
                  {authLoading ? 'VERIFYING...' : 'CONTINUE'} <ArrowRight size={18} />
                </button>
              </motion.form>
            )}

            {step === 'register' && (
              <motion.form key="register" onSubmit={handleRegisterSubmit} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-12">
                <div className="space-y-3 text-center">
                  <h3 className="text-3xl font-serif text-black italic">Tell us your name</h3>
                </div>
                <div className="relative">
                  <User className="absolute left-8 top-1/2 -translate-y-1/2 text-[#ccc]" size={20} />
                  <input 
                    type="text" required placeholder="Full Name"
                    value={name} onChange={(e) => setName(e.target.value)}
                    className="w-full bg-white border border-black/[0.1] rounded-[2rem] py-7 pl-20 pr-10 text-xl focus:outline-none focus:border-black transition-all shadow-sm font-semibold"
                  />
                </div>
                <button disabled={authLoading} className="luxury-button w-full flex items-center justify-center gap-4 !py-8 shadow-2xl !text-[11px] tracking-[0.3em]">
                  NEXT <ArrowRight size={18} />
                </button>
              </motion.form>
            )}

            {step === 'complete' && (
              <motion.div key="complete" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="py-24 text-center space-y-8">
                <motion.div animate={{ scale: [1, 1.2, 1], opacity: [1, 0.5, 1] }} transition={{ repeat: Infinity, duration: 2 }}>
                  <CheckCircle2 size={72} className="text-[#d4af37] mx-auto" />
                </motion.div>
                <div className="space-y-4">
                  <p className="text-black text-sm font-black uppercase tracking-[1em]">Success</p>
                  <p className="text-[#999] text-[10px] font-bold uppercase tracking-[0.5em] animate-pulse">Redirecting...</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Exclusive Footer Trust Signals */}
        <div className="grid grid-cols-3 gap-12 max-w-2xl mx-auto border-t border-black/[0.05] pt-16 mt-16 text-center">
          <Feature icon={<Zap size={20} />} label="Instant" />
          <Feature icon={<ShieldCheck size={20} />} label="Secure" />
          <Feature icon={<ShoppingBag size={20} />} label="Official" />
        </div>

        <div className="text-center mt-20 opacity-10">
          <p className="text-[8px] font-black tracking-[1.5em] text-black uppercase">© 2026 {shop?.shopName || 'Merchant Portal'}</p>
        </div>
      </motion.div>
    </div>
  );
};

const Feature = ({ icon, label }) => (
  <div className="flex flex-col items-center gap-4 group">
    <div className="text-black bg-black/5 w-12 h-12 rounded-2xl flex items-center justify-center group-hover:bg-black group-hover:text-[#d4af37] transition-all border border-black/[0.08] shadow-sm">
      {icon}
    </div>
    <p className="text-[9px] font-black uppercase tracking-[0.3em] text-black/40 group-hover:text-black transition-colors">{label}</p>
  </div>
);

export default ScanPage;
